import type {
  GetApiV1ThreeLetterCodePartnerAdminsClientFeatureFlags200,
  GetApiV1ThreeLetterCodePartnerAdminsContractPlans200,
  GetApiV1ThreeLetterCodePartnerAdminsGroups200,
  GetApiV1ThreeLetterCodePartnerAdminsPartnerBranches200,
  GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200,
  GetApiV1ThreeLetterCodePublicPrefectures200,
} from '@fukuri-admins/apis/client/gen/model';
import { isLastDayOfMonth } from '@fukuri-admins/utils/common/date';
import { getFeatureFlagLabel } from '~/utils/clientFeatureFlagUtils';
import { CreateClientFormPresentation } from '../../presentations';

type PresentationProps = React.ComponentProps<typeof CreateClientFormPresentation>;

type IConvertToPresentationProps = (args: {
  /** 都道府県一覧 */
  prefectures: GetApiV1ThreeLetterCodePublicPrefectures200;
  /** 取引店舗一覧 */
  partnerBranches: GetApiV1ThreeLetterCodePartnerAdminsPartnerBranches200;
  /** グループ一覧 */
  groups: GetApiV1ThreeLetterCodePartnerAdminsGroups200;
  /** クライアントの機能フラグ */
  clientFeatureFlags: GetApiV1ThreeLetterCodePartnerAdminsClientFeatureFlags200;
  /** パートナーの機能フラグ */
  partnerFeatureFlags: GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200;
  /** 契約プラン一覧 */
  contractPlans: GetApiV1ThreeLetterCodePartnerAdminsContractPlans200;
}) => {
  prefectureOptions: PresentationProps['prefectureOptions'];
  partnerBranchOptions: PresentationProps['partnerBranchOptions'];
  groupOptions: PresentationProps['groupOptions'];
  featureFlagOptions: PresentationProps['featureFlagOptions'];
  contractPlanOptions: PresentationProps['contractPlanOptions'];
  endedOnDisabledDate: PresentationProps['endedOnDisabledDate'];
};

/**
 * hooks の状態をプレゼンテーション用の props に変換する
 *
 * - contractPlanOptions: 廃止済み（discontinued_at !== 0）のプランは新規契約に指定できないため除外する。
 *   新規作成では初期選択値が無いため、EditClientContractTerm にある「現在値が一覧に無い場合の補完」
 *   ロジック（PEN-1159）は不要
 * - endedOnDisabledDate: サーバーが月末日以外の終了日を受け付けないため、月末日以外を選択不可にする
 */
export const convertToPresentationProps: IConvertToPresentationProps = ({
  prefectures,
  partnerBranches,
  groups,
  clientFeatureFlags,
  partnerFeatureFlags,
  contractPlans,
}) => ({
  prefectureOptions: prefectures.prefectures
    .filter((p): p is Required<typeof p> => !!p.code && !!p.name)
    .map(({ code, name }) => ({ value: code, label: name })),
  partnerBranchOptions: partnerBranches.partner_branches.map(({ id, name }) => ({
    value: String(id),
    label: name,
  })),
  groupOptions: groups.groups.map(({ id, name, code }) => ({
    value: String(id),
    label: `${name}（${code}）`,
  })),
  featureFlagOptions: clientFeatureFlags.client_feature_flags
    .filter(({ feature_key }) =>
      partnerFeatureFlags.partner_feature_flags.some(
        pf => pf.feature_key === feature_key && pf.enabled
      )
    )
    .map(({ feature_key }) => ({
      value: String(feature_key),
      label: getFeatureFlagLabel(feature_key),
    })),
  contractPlanOptions: contractPlans.contract_plans
    .filter(plan => plan.discontinued_at === 0)
    .map(plan => ({ value: String(plan.id), label: plan.name })),
  endedOnDisabledDate: date => !isLastDayOfMonth(date),
});
